import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import accountApi from '../../services/accountApi';

const PHASE = { form: 'form', review: 'review', success: 'success' };

const INITIAL = {
  name: '',
  slug: '',
  slugTouched: false,
  description: '',
  internalLabel: '',
  partnerType: 'studio',
  status: 'active',
  capabilityBundle: 'standard',
  serviceTier: 'standard',
  ownerMode: 'existing_user',
  ownerEmail: '',
  ownerLookup: null,
  teamRows: [{ email: '', role: 'developer' }],
  maxConcurrentJobs: '',
  compatibilityPolicy: '',
  platformNotes: '',
};

function slugifyName(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function PlatformPartnerOnboardingPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(PHASE.form);
  const [form, setForm] = useState(INITIAL);
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingOptions(true);
      try {
        const data = await accountApi.getPlatformPartnerOnboardingOptions();
        if (!cancelled) setOptions(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load onboarding options');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!form.slugTouched) {
      setForm((prev) => ({ ...prev, slug: slugifyName(prev.name) }));
    }
  }, [form.name, form.slugTouched]);

  const invitableRoles = options?.invitable_roles || [];

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validateForm = () => {
    if (!form.name.trim()) return 'Partner name is required.';
    if (!form.slug.trim()) return 'Slug is required.';
    if (!form.ownerEmail.trim()) return 'Owner email is required.';
    if (form.ownerMode === 'existing_user' && !form.ownerLookup) {
      return 'Look up an existing user before continuing.';
    }
    const emails = [form.ownerEmail.trim().toLowerCase()];
    for (const row of form.teamRows) {
      if (!row.email.trim()) continue;
      if (!row.role) return 'Each team invitation needs a role.';
      const key = row.email.trim().toLowerCase();
      if (emails.includes(key)) return 'Duplicate invitation emails are not allowed.';
      emails.push(key);
    }
    return null;
  };

  const buildPayload = () => ({
    partner: {
      name: form.name.trim(),
      slug: form.slug.trim(),
      type: form.partnerType,
      status: form.status,
      description: form.description.trim(),
      internal_label: form.internalLabel.trim(),
      platform_notes: form.platformNotes.trim(),
    },
    capability_bundle: form.capabilityBundle,
    service_tier: form.serviceTier,
    owner: {
      mode: form.ownerMode,
      email: form.ownerEmail.trim(),
    },
    invitations: form.teamRows
      .filter((r) => r.email.trim())
      .map((r) => ({ email: r.email.trim(), role: r.role })),
    defaults: {
      max_concurrent_jobs: form.maxConcurrentJobs || undefined,
      compatibility_policy: form.compatibilityPolicy || undefined,
      usage_visibility: true,
    },
  });

  const handleLookup = async () => {
    setFieldError(null);
    setError(null);
    setLookingUp(true);
    try {
      const user = await accountApi.lookupPlatformPartnerOwner({
        email: form.ownerEmail.trim(),
      });
      update({ ownerLookup: user, ownerMode: 'existing_user' });
    } catch (err) {
      update({ ownerLookup: null });
      setFieldError(err.message || 'No active user matches that email.');
    } finally {
      setLookingUp(false);
    }
  };

  const goReview = (event) => {
    event.preventDefault();
    const msg = validateForm();
    if (msg) {
      setFieldError(msg);
      return;
    }
    setFieldError(null);
    setPhase(PHASE.review);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await accountApi.onboardPlatformPartner(buildPayload());
      setResult(data);
      setPhase(PHASE.success);
    } catch (err) {
      setError(err.message || 'Onboarding failed');
      setPhase(PHASE.form);
    } finally {
      setSubmitting(false);
    }
  };

  const bundleLabel = useMemo(() => {
    const row = (options?.capability_bundles || []).find(
      (b) => b.code === form.capabilityBundle
    );
    return row ? row.display_name : form.capabilityBundle;
  }, [options, form.capabilityBundle]);

  if (loadingOptions) {
    return (
      <div className="loading-state">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading partner onboarding…
      </div>
    );
  }

  if (phase === PHASE.success && result) {
    const partner = result.partner || {};
    return (
      <div>
        <h1 className="product-page-title">Partner created</h1>
        <p className="product-page-subtitle">
          {partner.name} ({partner.slug}) is ready for the next onboarding actions.
        </p>
        {result.warnings && result.warnings.length > 0 && (
          <Alert variant="warning">
            Partner created with warnings:
            <ul className="mb-0">
              {result.warnings.map((w, idx) => (
                <li key={idx}>
                  {w.message} ({w.code})
                </li>
              ))}
            </ul>
          </Alert>
        )}
        <div className="product-card mb-3">
          <p className="mb-1">
            <strong>Status:</strong> {partner.status}
          </p>
          <p className="mb-1">
            <strong>Type:</strong> {partner.partner_type || partner.tenant_type}
          </p>
          <p className="mb-1">
            <strong>Capability bundle:</strong> {result.capability_bundle}
          </p>
          <p className="mb-1">
            <strong>Owner:</strong> {result.owner?.email} ({result.owner?.status})
          </p>
          <p className="mb-0">
            <strong>Team invitations:</strong> {(result.invitations || []).length}
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button
            as={Link}
            to={`/platform/partners/${partner.id}`}
            variant="primary"
          >
            Open Partner detail
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate('/platform/partners')}>
            Back to Partners
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => {
              setForm(INITIAL);
              setResult(null);
              setPhase(PHASE.form);
            }}
          >
            Create another
          </Button>
        </div>
      </div>
    );
  }

  if (phase === PHASE.review) {
    return (
      <div>
        <h1 className="product-page-title">Review Partner onboarding</h1>
        <p className="product-page-subtitle">Confirm configuration before creating the Partner.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="product-card mb-3">
          <h2 className="h6">Identity</h2>
          <p className="mb-1">
            <strong>{form.name}</strong> · <span className="mono">{form.slug}</span>
          </p>
          <p className="mb-0 text-muted">{form.description || 'No description'}</p>
        </div>
        <div className="product-card mb-3">
          <h2 className="h6">Type, status, and capabilities</h2>
          <p className="mb-1">
            Type: {form.partnerType} · Status: {form.status}
          </p>
          <p className="mb-1">
            Bundle: {bundleLabel} · Tier: {form.serviceTier}
          </p>
        </div>
        <div className="product-card mb-3">
          <h2 className="h6">Owner</h2>
          <p className="mb-0">
            {form.ownerMode === 'existing_user' ? 'Existing user' : 'Invitation'} —{' '}
            {form.ownerEmail}
            {form.ownerLookup ? ` (${form.ownerLookup.display_name})` : ''}
          </p>
        </div>
        <div className="product-card mb-3">
          <h2 className="h6">Team invitations</h2>
          {form.teamRows.filter((r) => r.email.trim()).length === 0 ? (
            <p className="mb-0 text-muted">None</p>
          ) : (
            <ul className="mb-0">
              {form.teamRows
                .filter((r) => r.email.trim())
                .map((r) => (
                  <li key={`${r.email}-${r.role}`}>
                    {r.email} · {r.role}
                  </li>
                ))}
            </ul>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => setPhase(PHASE.form)} disabled={submitting}>
            Back
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating…
              </>
            ) : (
              'Confirm and create Partner'
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-2">
        <Link to="/platform/partners">← Partners</Link>
      </p>
      <h1 className="product-page-title">Create Partner</h1>
      <p className="product-page-subtitle">
        Platform onboarding for a new Partner with owner assignment, optional team invitations,
        and capability defaults.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}
      {fieldError && <Alert variant="warning">{fieldError}</Alert>}

      <Form onSubmit={goReview}>
        <div className="product-card mb-3">
          <h2 className="h5 mb-3">1. Partner identity</h2>
          <Form.Group className="mb-3" controlId="onboard-partner-name">
            <Form.Label>Partner name</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="onboard-slug">
            <Form.Label>Slug</Form.Label>
            <Form.Control
              value={form.slug}
              onChange={(e) => update({ slug: e.target.value, slugTouched: true })}
              required
            />
            <Form.Text muted>Preview: /platform/partners/… · {form.slug || '…'}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="onboard-description">
            <Form.Label>Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-0" controlId="onboard-internal-label">
            <Form.Label>Internal label (optional)</Form.Label>
            <Form.Control
              value={form.internalLabel}
              onChange={(e) => update({ internalLabel: e.target.value })}
            />
          </Form.Group>
        </div>

        <div className="product-card mb-3">
          <h2 className="h5 mb-3">2. Type and status</h2>
          <Form.Group className="mb-3" controlId="onboard-type">
            <Form.Label>Partner type</Form.Label>
            <Form.Select
              value={form.partnerType}
              onChange={(e) => update({ partnerType: e.target.value })}
            >
              {(options?.partner_types || []).map((t) => (
                <option key={t.code} value={t.code}>
                  {t.display_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-0" controlId="onboard-status">
            <Form.Label>Initial status</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(e) => update({ status: e.target.value })}
            >
              {(options?.statuses || []).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.display_name}
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>Archived is not available as an initial creation state.</Form.Text>
          </Form.Group>
        </div>

        <div className="product-card mb-3">
          <h2 className="h5 mb-3">3. Product and execution access</h2>
          <Form.Group className="mb-3" controlId="onboard-bundle">
            <Form.Label>Capability bundle</Form.Label>
            <Form.Select
              value={form.capabilityBundle}
              onChange={(e) => update({ capabilityBundle: e.target.value })}
            >
              {(options?.capability_bundles || []).map((b) => (
                <option key={b.code} value={b.code}>
                  {b.display_name}
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>
              Backend-defined product capabilities. Not Partner role permissions.
            </Form.Text>
          </Form.Group>
          {(options?.capability_bundles || [])
            .filter((b) => b.code === form.capabilityBundle)
            .map((b) => (
              <p key={b.code} className="small text-muted mb-0">
                {b.description} · {b.capabilities.join(', ')}
              </p>
            ))}
        </div>

        <div className="product-card mb-3">
          <h2 className="h5 mb-3">4. Initial Partner Owner</h2>
          <Form.Group className="mb-3" controlId="onboard-owner-mode">
            <Form.Label>Owner strategy</Form.Label>
            <Form.Select
              value={form.ownerMode}
              onChange={(e) =>
                update({
                  ownerMode: e.target.value,
                  ownerLookup: e.target.value === 'invite' ? null : form.ownerLookup,
                })
              }
            >
              <option value="existing_user">Assign existing user</option>
              <option value="invite">Invite new owner (invitation-only signup)</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3" controlId="onboard-owner-email">
            <Form.Label>Owner email</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="email"
                value={form.ownerEmail}
                onChange={(e) => update({ ownerEmail: e.target.value, ownerLookup: null })}
                required
              />
              {form.ownerMode === 'existing_user' && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleLookup}
                  disabled={lookingUp}
                >
                  {lookingUp ? 'Looking up…' : 'Look up'}
                </Button>
              )}
            </div>
          </Form.Group>          {form.ownerLookup && (
            <Alert variant="success" className="mb-0">
              Matched {form.ownerLookup.display_name} ({form.ownerLookup.email})
            </Alert>
          )}
          {form.ownerMode === 'invite' && (
            <Form.Text muted>
              Creates a pending Partner Owner invitation. No password is set by the platform.
            </Form.Text>
          )}
        </div>

        <div className="product-card mb-3">
          <h2 className="h5 mb-3">5. Team invitations (optional)</h2>
          <Table size="sm" className="product-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {form.teamRows.map((row, idx) => (
                <tr key={idx}>
                  <td>
                    <Form.Control
                      type="email"
                      value={row.email}
                      onChange={(e) => {
                        const teamRows = [...form.teamRows];
                        teamRows[idx] = { ...row, email: e.target.value };
                        update({ teamRows });
                      }}
                    />
                  </td>
                  <td>
                    <Form.Select
                      value={row.role}
                      onChange={(e) => {
                        const teamRows = [...form.teamRows];
                        teamRows[idx] = { ...row, role: e.target.value };
                        update({ teamRows });
                      }}
                    >
                      {invitableRoles.map((r) => (
                        <option key={r.code} value={r.code}>
                          {r.display_name || r.code}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-danger"
                      onClick={() =>
                        update({
                          teamRows: form.teamRows.filter((_, i) => i !== idx),
                        })
                      }
                      disabled={form.teamRows.length <= 1}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button
            type="button"
            size="sm"
            variant="outline-secondary"
            onClick={() =>
              update({
                teamRows: [...form.teamRows, { email: '', role: 'developer' }],
              })
            }
          >
            Add invitation
          </Button>
        </div>

        <div className="product-card mb-3">
          <h2 className="h5 mb-3">6. Limits and defaults</h2>
          <Form.Group className="mb-3">
            <Form.Label>Service tier</Form.Label>
            <Form.Select
              value={form.serviceTier}
              onChange={(e) => update({ serviceTier: e.target.value })}
            >
              {(options?.service_tiers || []).map((t) => (
                <option key={t.code} value={t.code}>
                  {t.display_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Max concurrent jobs (metadata)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.maxConcurrentJobs}
              onChange={(e) => update({ maxConcurrentJobs: e.target.value })}
            />
            <Form.Text muted>Stored as metadata; not enforced by this slice.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Default compatibility policy id (metadata)</Form.Label>
            <Form.Control
              value={form.compatibilityPolicy}
              onChange={(e) => update({ compatibilityPolicy: e.target.value })}
              placeholder="Optional policy code"
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Platform-only notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.platformNotes}
              onChange={(e) => update({ platformNotes: e.target.value })}
            />
          </Form.Group>
        </div>

        <div className="d-flex gap-2">
          <Button type="submit" variant="primary">
            Review configuration
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={() => navigate('/platform/partners')}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default PlatformPartnerOnboardingPage;
